import {
  Box,
  Card,
  CardBody,
  Heading,
  Text,
  VStack,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
  Button,
  useDisclosure,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { HelpCircle, PhoneCall } from 'lucide-react'
import { AliceCallOutModal, type AliceCallPayload } from '@/components/client/AliceCallOutModal'

const FAQ_ITEMS = [
  {
    q: 'What is the My Requests page?',
    a: 'The My Requests page (Support → My Requests) is where you view, create, and manage your support ticket requests. You can submit new requests, save drafts, edit drafts before submitting, and track the status of your requests.',
  },
  {
    q: 'How do I create a new request?',
    a: 'Click the "Create Request" button at the top of the My Requests page. Fill in the form: select a Service type (e.g. Breakfix, Install), choose one or more Categories, describe the issue, optionally set preferred schedule and contact details, and add attachments if needed. You can either "Submit request" to send it immediately or "Save as draft" to finish later.',
  },
  {
    q: 'What is the difference between saving a draft and submitting?',
    a: 'Saving as draft stores your request without sending it to the support team. You can edit drafts anytime by clicking Edit on the row. Once you submit a request, it is sent and can no longer be edited from this page; only drafts can be edited.',
  },
  {
    q: 'How do I search or filter requests?',
    a: 'Use the search box to find requests by request number, description, contact name, email, or other text. You can filter by Status using the filter on the Status column (New, Pending, In Progress, Resolved, Closed, Cancelled, Draft). Use the "All" and "Trash" tabs to switch between active requests and deleted (archived) ones.',
  },
  {
    q: 'What do the statuses mean?',
    a: 'Draft = not yet submitted. New = just submitted. Pending = waiting for action. In Progress = being worked on. Resolved = completed. Closed = request closed. Cancelled = request was cancelled.',
  },
  {
    q: 'Can I attach files to a request?',
    a: 'Yes. When creating or editing a request, use the attachments area to add files (e.g. screenshots, documents). Supported file types and size limits apply. You can remove an attachment before submitting by using the remove button next to it.',
  },
  {
    q: 'What are Service type and Category?',
    a: 'Service type is the kind of support (e.g. Breakfix, Install). Category describes the area (e.g. Software, Hardware). Select the option that best matches your issue so your request is routed correctly.',
  },
  {
    q: 'How do I edit or delete a request?',
    a: 'Only drafts can be edited: click the Edit action on the row to open the form. To delete a request, use the Delete (trash) action; the request is moved to Trash. You can view trashed requests in the Trash tab and restore or permanently delete them from there.',
  },
  {
    q: 'What are Date created and Last updated?',
    a: 'Date created is when the request was first created. Last updated shows how long after submission the request was last updated (e.g. when status or details changed).',
  },
]

const ALICE_FAQ_ITEMS = [
  {
    q: 'What is the "Call out" feature?',
    a: 'The Call out feature sends details to ALICE Voice AI so ALICE can call the contact and ask the questionnaire for faster issue intake.',
  },
  {
    q: 'When should I use "Call out"?',
    a: 'Use it when you want ALICE to gather ticket details by phone, either for an initial request intake or a follow-up request with an existing ticket.',
  },
  {
    q: 'What happens during a new request call?',
    a: 'ALICE calls the provided contact number, asks the questionnaire, and your team can use the responses to create a complete ticket request.',
  },
  {
    q: 'What happens during a follow-up call?',
    a: 'ALICE calls the existing ticket contact to collect missing details and clarifications, then sends the updated answers back.',
  },
  {
    q: 'Where can I trigger a Call out?',
    a: 'You can trigger Call out from My Requests header, Create Ticket modal footer, ticket row action menu, and this Help Center page.',
  },
  {
    q: 'What information is required?',
    a: 'A valid contact number is required. Name and email are optional but recommended for better questionnaire pre-filling.',
  },
]

export default function HelpCenterPage() {
  const colors = useThemeColors()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const defaultPayload: AliceCallPayload = {
    webhook_source: 'actionlabs-support-client',
    call_mode: 'new_request',
    client_code: null,
    call_id: null,
    ticket: {
      id: null,
      request_number: null,
      service_type: null,
      category: null,
      description: null,
      status: null,
    },
    caller: {
      name: '',
      contact_number: '',
      email: '',
    },
    questionnaire: [],
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="auto">
        <VStack align="stretch" spacing={6} maxW="900px" mx="auto">
          <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack spacing={3} align="center">
                  <Box color="blue.500">
                    <HelpCircle size={32} />
                  </Box>
                  <Box>
                    <Heading size="lg" color={colors.text}>
                      Help Center
                    </Heading>
                    <Text color={colors.textSecondary} fontSize="sm">
                      Frequently asked questions about using the portal
                    </Text>
                  </Box>
                </HStack>
                <Text color={colors.text} fontSize="sm">
                  This guide covers how to use the <strong>My Requests</strong> page to create and manage your support requests. Need to open the requests page?{' '}
                  <Link as={RouterLink} to="/my-request" color="blue.500">
                    Go to My Requests
                  </Link>
                </Text>
                <Button
                  leftIcon={<PhoneCall size={16} />}
                  colorScheme="green"
                  size="sm"
                  alignSelf="flex-start"
                  onClick={onOpen}
                >
                  Call out to ALICE
                </Button>
              </VStack>
            </CardBody>
          </Card>

          <Heading size="md" color={colors.text} alignSelf="start">
            Requests management (My Requests)
          </Heading>

          <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
            <CardBody py={0} px={0}>
              <Accordion allowMultiple reduceMotion>
                {FAQ_ITEMS.map((item, index) => (
                  <AccordionItem key={index} borderColor={colors.border}>
                    <AccordionButton
                      py={4}
                      px={6}
                      _expanded={{ bg: colors.bg }}
                      _hover={{ bg: colors.bg }}
                    >
                      <Box flex="1" textAlign="left" fontWeight="medium" color={colors.text}>
                        {item.q}
                      </Box>
                      <AccordionIcon color={colors.textSecondary} />
                    </AccordionButton>
                    <AccordionPanel pb={4} px={6} pt={0} color={colors.textSecondary} fontSize="sm">
                      {item.a}
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardBody>
          </Card>

          <Heading size="md" color={colors.text} alignSelf="start">
            ALICE Voice AI Call out
          </Heading>

          <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
            <CardBody py={0} px={0}>
              <Accordion allowMultiple reduceMotion>
                {ALICE_FAQ_ITEMS.map((item, index) => (
                  <AccordionItem key={`alice-${index}`} borderColor={colors.border}>
                    <AccordionButton
                      py={4}
                      px={6}
                      _expanded={{ bg: colors.bg }}
                      _hover={{ bg: colors.bg }}
                    >
                      <Box flex="1" textAlign="left" fontWeight="medium" color={colors.text}>
                        {item.q}
                      </Box>
                      <AccordionIcon color={colors.textSecondary} />
                    </AccordionButton>
                    <AccordionPanel pb={4} px={6} pt={0} color={colors.textSecondary} fontSize="sm">
                      {item.a}
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardBody>
          </Card>
        </VStack>
      </Box>
      <AliceCallOutModal isOpen={isOpen} onClose={onClose} initialPayload={defaultPayload} />
    </AdminLayout>
  )
}
